import React, { useState, useMemo } from 'react';
import { 
  BarChart3, FileText, Download, Calendar, TrendingUp, Filter,
  PieChart, Activity, Users, CheckCircle, Clock, MessageSquare,
  Target, Award, Zap, ArrowUp, ArrowDown, Minus, Star,
  Mail, Phone, MessageCircle, Eye, RefreshCw, AlertTriangle,
  ThumbsUp, ThumbsDown, Flame, Shield
} from 'lucide-react';
import { GlobalStats, DetailedAuditorStats, AdvancedFilters, FilterPreset, ViewFilterPresets } from '../types/AdminTypes';

interface ReportsViewProps {
  globalStats: GlobalStats;
  auditorStats: DetailedAuditorStats[];
  filters: AdvancedFilters;
  filterPresets: ViewFilterPresets;
  activePreset: string;
  onApplyPreset: (preset: FilterPreset) => void;
  onApplyCustomDates: (dateFrom: string, dateTo: string) => void;
}

const ReportsView: React.FC<ReportsViewProps> = ({ 
  globalStats, 
  auditorStats, 
  filters,
  filterPresets,
  activePreset,
  onApplyPreset,
  onApplyCustomDates
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [selectedReport, setSelectedReport] = useState<string>('overview');

  // 🎯 NORME DE PRODUCTIVITÉ
  const PRODUCTION_TARGET = 250; // tâches par jour minimum

  // 📊 CALCULS AVANCÉS AVEC NORME DE PRODUCTIVITÉ
  const analytics = useMemo(() => {
    // Classification selon la norme de 250 AUDITS TRAITÉS/jour
    const auditorsWithProcessedTasks = auditorStats.map(auditor => ({
      ...auditor,
      // ✅ CORRECTION : Productivité = validées + commentées (audits traités)
      dailyProcessedTasks: auditor.validatedTasks + auditor.commentedTasks,
      // Taux de traitement pour les analyses de qualité
      processingRate: auditor.totalTasks > 0 ? 
        ((auditor.validatedTasks + auditor.commentedTasks) / auditor.totalTasks) * 100 : 0
    }));
  
    // Classifications basées sur averageTasksPerDay (pour objectifs quotidiens)
    const excellentPerformers = auditorsWithProcessedTasks.filter(a => a.averageTasksPerDay >= PRODUCTION_TARGET * 1.2); // 300+ tâches/jour
    const goodPerformers = auditorsWithProcessedTasks.filter(a => a.averageTasksPerDay >= PRODUCTION_TARGET && a.averageTasksPerDay < PRODUCTION_TARGET * 1.2); // 250-299 tâches/jour
    const belowTarget = auditorsWithProcessedTasks.filter(a => a.averageTasksPerDay < PRODUCTION_TARGET && a.averageTasksPerDay > 0); // <250 tâches/jour
    const inactive = auditorsWithProcessedTasks.filter(a => a.averageTasksPerDay === 0); // 0 tâche/jour
  
    // ✅ RÉPONSE À VOS DEMANDES EXACTES :
  
    // 1. Production TOTALE = somme des totalTasks (ce que vous vouliez : 6 + 5 = 11)
    const totalProductionPeriod = auditorsWithProcessedTasks.reduce((sum, a) => sum + a.totalTasks, 0);
  
    // 2. Capacité QUOTIDIENNE = 3 auditeurs × 250 = 750/jour (ce que vous vouliez)
    const maxCapacityDaily = globalStats.activeAuditors * PRODUCTION_TARGET;
  
    // 3. Production QUOTIDIENNE moyenne = somme des averageTasksPerDay (1 + 1 = 2/jour)
    const currentUsageDaily = auditorsWithProcessedTasks.reduce((sum, a) => sum + a.averageTasksPerDay, 0);
  
    // 4. Utilisation capacité quotidienne = 2/750 = 0.3%
    const capacityUtilizationDaily = maxCapacityDaily > 0 ? (currentUsageDaily / maxCapacityDaily) * 100 : 0;
  
    // Pourcentage d'atteinte de l'objectif global basé sur performance quotidienne
    const targetAchievementPercentage = maxCapacityDaily > 0 ? (currentUsageDaily / maxCapacityDaily) * 100 : 0;
  
    // Top performers selon la productivité quotidienne + qualité
    const topPerformers = auditorsWithProcessedTasks
      .filter(a => a.totalTasks > 0)
      .sort((a, b) => {
        // Critère principal : tâches par jour + qualité
        const aScore = (a.averageTasksPerDay / PRODUCTION_TARGET) * 0.7 + (a.validationRate / 100) * 0.3;
        const bScore = (b.averageTasksPerDay / PRODUCTION_TARGET) * 0.7 + (b.validationRate / 100) * 0.3;
        return bScore - aScore;
      })
      .slice(0, 5);
  
    // Auditeurs nécessitant une attention (productivité quotidienne insuffisante)
    const needsAttention = auditorsWithProcessedTasks.filter(a => 
      a.averageTasksPerDay < PRODUCTION_TARGET * 0.8 || // <200 tâches/jour
      a.validationRate < 70 || // <70% validation
      a.totalTasks === 0 // Inactifs
    );
  
    // Auditeurs champions (>= objectif quotidien + excellente qualité)
    const champions = auditorsWithProcessedTasks.filter(a => 
      a.averageTasksPerDay >= PRODUCTION_TARGET && 
      a.validationRate >= 90
    );
  
    return {
      auditorsWithProcessedTasks,
      excellentPerformers,
      goodPerformers,
      belowTarget,
      inactive,
      targetAchievementPercentage,
      topPerformers,
      needsAttention,
      champions,
  
      // ✅ VOS DEMANDES SPÉCIFIQUES :
      
      // Pour l'affichage "Production Actuelle" = TOTAL des tâches sur la période
      currentUsage: totalProductionPeriod, // 11 tâches (6 + 5)
      
      // Pour l'affichage "Capacité Max" = Capacité QUOTIDIENNE  
      maxCapacity: maxCapacityDaily, // 750/jour (3 × 250)
      
      // Pour l'affichage utilisation = basé sur performance quotidienne
      capacityUtilization: capacityUtilizationDaily, // 0.3% (2/750)
  
      // Métriques quotidiennes supplémentaires (si besoin d'afficher ailleurs)
      dailyCurrentUsage: currentUsageDaily, // 2/jour
      dailyMaxCapacity: maxCapacityDaily, // 750/jour
      
      // Statistiques de performance
      avgProcessingProductivity: auditorsWithProcessedTasks.length > 0 ? 
        auditorsWithProcessedTasks.reduce((sum, a) => sum + a.averageTasksPerDay, 0) / auditorsWithProcessedTasks.length : 0,
      totalProcessed: totalProductionPeriod // 11 tâches total (même que currentUsage)
    };
  }, [auditorStats, globalStats]);
  
  // 🎨 Fonction pour obtenir la couleur selon performance d'audits traités
  const getPerformanceColor = (processedPerDay: number, validationRate: number) => {
    if (processedPerDay >= PRODUCTION_TARGET && validationRate >= 90) return 'from-green-500 to-emerald-600'; // Champion
    if (processedPerDay >= PRODUCTION_TARGET) return 'from-blue-500 to-blue-600'; // Objectif atteint
    if (processedPerDay >= PRODUCTION_TARGET * 0.8) return 'from-yellow-500 to-orange-500'; // Proche objectif
    return 'from-red-500 to-red-600'; // En dessous objectif
  };

  // 📈 Fonction pour calculer la tendance avec norme
  const getTrendIcon = (value: number) => {
    if (value > 0) return <ArrowUp className="w-4 h-4 text-green-500" />;
    if (value < 0) return <ArrowDown className="w-4 h-4 text-red-500" />;
    return <Minus className="w-4 h-4 text-gray-500" />;
  };

  // 🏆 Badge de performance selon norme
  const getPerformanceBadge = (processedPerDay: number, validationRate: number) => {
    if (processedPerDay >= PRODUCTION_TARGET * 1.2 && validationRate >= 90) {
      return { icon: Flame, label: 'Champion', color: 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white' };
    }
    if (processedPerDay >= PRODUCTION_TARGET && validationRate >= 80) {
      return { icon: ThumbsUp, label: 'Excellent', color: 'bg-gradient-to-r from-green-500 to-green-600 text-white' };
    }
    if (processedPerDay >= PRODUCTION_TARGET * 0.8) {
      return { icon: Shield, label: 'Correct', color: 'bg-gradient-to-r from-blue-500 to-blue-600 text-white' };
    }
    return { icon: ThumbsDown, label: 'À améliorer', color: 'bg-gradient-to-r from-red-500 to-red-600 text-white' };
  };

  // 📤 Export avec norme de productivité
  const exportReport = (type: string) => {
    let csvContent = '';
    let filename = '';

    switch (type) {
      case 'productivity':
        csvContent = [
          'Nom,Email,Tâches/Jour,Objectif (250),Atteinte %,Statut,Validation %,Score Performance',
          ...auditorStats.map(a => {
            const achievementPercentage = (a.averageTasksPerDay / PRODUCTION_TARGET) * 100;
            const status = a.averageTasksPerDay >= PRODUCTION_TARGET ? 'Objectif Atteint' : 
                          a.averageTasksPerDay >= PRODUCTION_TARGET * 0.8 ? 'Proche Objectif' : 'En Dessous';
            const performanceScore = (a.averageTasksPerDay / PRODUCTION_TARGET) * 0.6 + (a.validationRate / 100) * 0.4;
            
            return [
              a.auditor.full_name,
              a.auditor.email,
              a.averageTasksPerDay,
              PRODUCTION_TARGET,
              `${achievementPercentage.toFixed(1)}%`,
              status,
              `${a.validationRate}%`,
              `${(performanceScore * 100).toFixed(1)}/100`
            ].join(',');
          })
        ].join('\n');
        filename = `rapport_productivite_norme250_${new Date().toISOString().split('T')[0]}.csv`;
        break;

      case 'capacity':
        csvContent = [
          'Métrique,Valeur,Norme/Objectif',
          `Capacité Maximale,${analytics.maxCapacity} tâches/jour,${PRODUCTION_TARGET} × ${globalStats.activeAuditors} auditeurs`,
          `Production Actuelle,${analytics.currentUsage.toFixed(0)} tâches/jour,${analytics.maxCapacity} tâches/jour`,
          `Utilisation Capacité,${analytics.capacityUtilization.toFixed(1)}%,100%`,
          `Auditeurs Objectif Atteint,${analytics.goodPerformers.length + analytics.excellentPerformers.length},${globalStats.activeAuditors}`,
          `Auditeurs Champions,${analytics.champions.length},${globalStats.activeAuditors}`,
          `Auditeurs Attention Requise,${analytics.needsAttention.length},0`
        ].join('\n');
        filename = `rapport_capacite_${new Date().toISOString().split('T')[0]}.csv`;
        break;

      default:
        return;
    }

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header avec indicateur de norme */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
      <div>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">📊 Rapports de Productivité</h2>
            <p className="text-gray-600 text-sm sm:text-base">
                Norme: <span className="font-bold text-blue-600">{PRODUCTION_TARGET} audits traités/jour</span> • 
                Période: {activePreset} • 
                Atteinte globale: <span className={`font-bold ${analytics.targetAchievementPercentage >= 100 ? 'text-green-600' : analytics.targetAchievementPercentage >= 80 ? 'text-orange-600' : 'text-red-600'}`}>
                {analytics.targetAchievementPercentage.toFixed(1)}%
                </span>
            </p>
            </div>
        
        <div className="flex items-center space-x-2">
          <button
            onClick={() => exportReport('productivity')}
            className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 flex items-center space-x-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Export Productivité</span>
          </button>
          <button
            onClick={() => exportReport('capacity')}
            className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 flex items-center space-x-2 transition-colors"
          >
            <Download className="w-4 h-4" />
            <span>Export Capacité</span>
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-4 py-2 border rounded-lg flex items-center space-x-2 transition-colors ${
              showFilters ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            <Filter className="w-4 h-4" />
            <span>Période</span>
          </button>
        </div>
      </div>

      {/* Alerte de performance globale */}
      {analytics.targetAchievementPercentage < 80 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-6 h-6 text-red-500" />
            <div>
              <h3 className="font-bold text-red-900">⚠️ Performance en Dessous de la Norme</h3>
              <p className="text-red-700 text-sm">
                L'équipe atteint seulement <strong>{analytics.targetAchievementPercentage.toFixed(1)}%</strong> de l'objectif de productivité. 
                <strong> {analytics.belowTarget.length + analytics.inactive.length}</strong> auditeurs nécessitent une attention immédiate.
              </p>
            </div>
          </div>
        </div>
      )}

      {analytics.targetAchievementPercentage >= 100 && (
        <div className="bg-green-50 border-l-4 border-green-500 p-4 rounded-lg">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-6 h-6 text-green-500" />
            <div>
              <h3 className="font-bold text-green-900">🎉 Objectif de Productivité Atteint !</h3>
              <p className="text-green-700 text-sm">
                L'équipe dépasse l'objectif avec <strong>{analytics.targetAchievementPercentage.toFixed(1)}%</strong> de performance. 
                <strong> {analytics.champions.length}</strong> auditeurs sont des champions !
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Filtres de période */}
      {showFilters && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              📅 Sélection de période pour l'analyse de productivité
            </label>
            <div className="flex flex-wrap gap-2">
              {filterPresets.presets.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => onApplyPreset(preset)}
                  className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                    activePreset === preset.label
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {preset.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Navigation des types de rapports */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="flex flex-wrap gap-2">
          {[
            { id: 'overview', label: '📊 Tableau de Bord', icon: BarChart3 },
            { id: 'performance', label: '🎯 Performance vs Norme', icon: Target },
            { id: 'capacity', label: '⚡ Analyse de Capacité', icon: Zap },
            { id: 'coaching', label: '🎓 Recommandations', icon: Award }
          ].map((report) => (
            <button
              key={report.id}
              onClick={() => setSelectedReport(report.id)}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors ${
                selectedReport === report.id
                  ? 'bg-blue-500 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <report.icon className="w-4 h-4" />
              <span>{report.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* 📊 TABLEAU DE BORD */}
      {selectedReport === 'overview' && (
        <div className="space-y-6">
          {/* KPIs de productivité avec norme */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl p-6 text-white relative overflow-hidden">
            <div className="flex items-center justify-between mb-4">
                <Target className="w-8 h-8" />
                <div className="text-right">
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                    analytics.targetAchievementPercentage >= 100 ? 'bg-green-500' :
                    analytics.targetAchievementPercentage >= 80 ? 'bg-yellow-500' : 'bg-red-500'
                }`}>
                    {analytics.targetAchievementPercentage >= 100 ? '✓' : '!'}
                </span>
                </div>
            </div>
            <p className="text-3xl font-bold">{analytics.targetAchievementPercentage.toFixed(1)}%</p>
            <p className="text-blue-100">Atteinte Objectif</p>
            <p className="text-xs text-blue-200 mt-1">Norme: {PRODUCTION_TARGET} audits traités/jour</p>
            <div className="absolute bottom-0 left-0 w-full h-1 bg-blue-400">
                <div 
                className="h-1 bg-white transition-all duration-1000"
                style={{ width: `${Math.min(analytics.targetAchievementPercentage, 100)}%` }}
                />
            </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <ThumbsUp className="w-8 h-8" />
                <Flame className="w-6 h-6 text-yellow-300" />
              </div>
              <p className="text-3xl font-bold">{analytics.excellentPerformers.length + analytics.goodPerformers.length}</p>
              <p className="text-green-100">Objectif Atteint</p>
              <p className="text-xs text-green-200 mt-1">
                Champions: {analytics.excellentPerformers.length} | Bons: {analytics.goodPerformers.length}
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-red-500 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <AlertTriangle className="w-8 h-8" />
                <span className="text-xs bg-white text-orange-600 px-2 py-1 rounded-full font-bold">
                  {analytics.belowTarget.length + analytics.inactive.length}
                </span>
              </div>
              <p className="text-3xl font-bold">{analytics.belowTarget.length}</p>
              <p className="text-orange-100">Sous l'Objectif</p>
              <p className="text-xs text-orange-200 mt-1">Inactifs: {analytics.inactive.length}</p>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl p-6 text-white">
              <div className="flex items-center justify-between mb-4">
                <Zap className="w-8 h-8" />
                {getTrendIcon(analytics.capacityUtilization - 100)}
              </div>
              <p className="text-3xl font-bold">{analytics.capacityUtilization.toFixed(0)}%</p>
              <p className="text-purple-100">Utilisation Capacité</p>
              <p className="text-xs text-purple-200 mt-1">
                {analytics.currentUsage.toFixed(0)}/{analytics.maxCapacity} tâches/jour
              </p>
            </div>
          </div>

          {/* Distribution selon norme de productivité */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">📊 Distribution par Performance</h3>
                <span className="text-sm text-gray-500">Norme: {PRODUCTION_TARGET}/jour</span>
              </div>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-lg border border-yellow-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <Flame className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-yellow-900">Champions</p>
                      <p className="text-xs text-yellow-700">≥ {PRODUCTION_TARGET * 1.2}/jour + 90% validation</p>
                    </div>
                  </div>
                  <span className="text-3xl font-bold text-yellow-800">{analytics.excellentPerformers.length}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-green-900">Objectif Atteint</p>
                      <p className="text-xs text-green-700">{PRODUCTION_TARGET}-{PRODUCTION_TARGET * 1.2 - 1}/jour</p>
                    </div>
                  </div>
                  <span className="text-3xl font-bold text-green-800">{analytics.goodPerformers.length}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-orange-900">Sous l'Objectif</p>
                      <p className="text-xs text-orange-700"> {PRODUCTION_TARGET}/jour</p>
                    </div>
                  </div>
                  <span className="text-3xl font-bold text-orange-800">{analytics.belowTarget.length}</span>
                </div>

                <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg border border-red-200">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                      <AlertTriangle className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="font-bold text-red-900">Inactifs</p>
                      <p className="text-xs text-red-700">0 tâche/jour</p>
                    </div>
                  </div>
                  <span className="text-3xl font-bold text-red-800">{analytics.inactive.length}</span>
                </div>
              </div>

              {/* Graphique de progression */}
              <div className="mt-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Progression vers l'objectif</span>
                  <span>{analytics.targetAchievementPercentage.toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-4">
                  <div className="flex h-4 rounded-full overflow-hidden">
                    <div 
                      className="bg-gradient-to-r from-green-500 to-green-600" 
                      style={{ 
                        width: `${Math.min((analytics.excellentPerformers.length + analytics.goodPerformers.length) / Math.max(globalStats.activeAuditors, 1) * 100, 100)}%` 
                      }}
                    ></div>
                    <div 
                      className="bg-gradient-to-r from-orange-500 to-red-500" 
                      style={{ 
                        width: `${Math.min((analytics.belowTarget.length + analytics.inactive.length) / Math.max(globalStats.activeAuditors, 1) * 100, 100)}%` 
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Champions de la productivité */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-gray-900">🏆 Champions de Productivité</h3>
                <span className="text-sm text-gray-500">Top Performance</span>
              </div>
              
              <div className="space-y-4">
                {analytics.topPerformers.slice(0, 5).map((auditor, index) => {
                  const badge = getPerformanceBadge(auditor.averageTasksPerDay, auditor.validationRate);
                  const targetPercentage = (auditor.averageTasksPerDay / PRODUCTION_TARGET) * 100;
                  
                  return (
                    <div key={auditor.auditor_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <div className="relative">
                          <div className={`w-12 h-12 bg-gradient-to-r ${getPerformanceColor(auditor.averageTasksPerDay, auditor.validationRate)} rounded-full flex items-center justify-center`}>
                            <span className="text-white font-bold text-sm">
                              {auditor.auditor.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                            </span>
                          </div>
                          {index < 3 && (
                            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-r from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-xs font-bold">{index + 1}</span>
                            </div>
                          )}
                        </div>
                        <div>
                          <p className="font-bold text-gray-900">{auditor.auditor.full_name}</p>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm font-bold text-blue-600">
                              {auditor.averageTasksPerDay}/jour
                            </span>
                            <span className={`text-xs px-2 py-1 rounded-full font-bold ${
                              targetPercentage >= 100 ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                            }`}>
                              {targetPercentage.toFixed(0)}%
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-bold ${badge.color}`}>
                          <badge.icon className="w-3 h-3" />
                          <span>{badge.label}</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1">{auditor.validationRate}% validation</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 🎯 PERFORMANCE VS NORME */}
      {selectedReport === 'performance' && (
        <div className="space-y-6">
          {/* Graphique de performance individuelle */}
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900">📊 Performance Individuelle vs Norme ({PRODUCTION_TARGET}/jour)</h3>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
              <RefreshCw className="w-4 h-4" />
               <span>Mise à jour en temps réel</span>
             </div>
           </div>

           <div className="space-y-4">
             {auditorStats
               .filter(a => a.totalTasks > 0)
               .sort((a, b) => b.averageTasksPerDay - a.averageTasksPerDay)
               .map((auditor) => {
                 const targetPercentage = (auditor.averageTasksPerDay / PRODUCTION_TARGET) * 100;
                 const badge = getPerformanceBadge(auditor.averageTasksPerDay, auditor.validationRate);
                 
                 return (
                   <div key={auditor.auditor_id} className="space-y-2">
                     <div className="flex items-center justify-between">
                       <div className="flex items-center space-x-3">
                         <div className={`w-10 h-10 bg-gradient-to-r ${getPerformanceColor(auditor.averageTasksPerDay, auditor.validationRate)} rounded-full flex items-center justify-center`}>
                           <span className="text-white text-xs font-bold">
                             {auditor.auditor.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                           </span>
                         </div>
                         <div>
                           <span className="font-medium">{auditor.auditor.full_name}</span>
                           <div className={`inline-flex items-center space-x-1 ml-2 px-2 py-1 rounded-full text-xs font-bold ${badge.color}`}>
                             <badge.icon className="w-3 h-3" />
                             <span>{badge.label}</span>
                           </div>
                         </div>
                       </div>
                       <div className="flex items-center space-x-4 text-sm">
                         <span className="text-gray-600">{auditor.averageTasksPerDay}/jour</span>
                         <span className={`font-bold px-2 py-1 rounded ${
                           targetPercentage >= 100 ? 'bg-green-100 text-green-800' :
                           targetPercentage >= 80 ? 'bg-yellow-100 text-yellow-800' :
                           'bg-red-100 text-red-800'
                         }`}>
                           {targetPercentage.toFixed(0)}%
                         </span>
                         <span className="text-green-600">{auditor.validationRate}%</span>
                       </div>
                     </div>
                     
                     {/* Barre de progression avec ligne d'objectif */}
                     <div className="relative">
                       <div className="w-full bg-gray-200 rounded-full h-4">
                         <div 
                           className={`bg-gradient-to-r ${getPerformanceColor(auditor.averageTasksPerDay, auditor.validationRate)} h-4 rounded-full transition-all duration-1000 relative overflow-hidden`}
                           style={{ width: `${Math.min(targetPercentage, 150)}%` }}
                         >
                           <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
                         </div>
                       </div>
                       {/* Ligne d'objectif */}
                       <div className="absolute top-0 left-0 w-full h-4 flex items-center">
                         <div 
                           className="h-6 w-0.5 bg-red-600 relative"
                           style={{ left: '66.67%' }} // 100% de l'objectif
                         >
                           <span className="absolute -top-6 -left-8 text-xs font-bold text-red-600">
                             Objectif
                           </span>
                         </div>
                       </div>
                     </div>
                     
                     {/* Détails de performance */}
                     <div className="flex justify-between text-xs text-gray-500">
                       <span>Total: {auditor.totalTasks} tâches</span>
                       <span>Validation: {auditor.validationRate}%</span>
                       <span>Temps moy: {auditor.avgProcessingTime}min</span>
                       <span className={`font-bold ${
                         auditor.averageTasksPerDay >= PRODUCTION_TARGET ? 'text-green-600' : 'text-red-600'
                       }`}>
                         {auditor.averageTasksPerDay >= PRODUCTION_TARGET ? 
                           `+${auditor.averageTasksPerDay - PRODUCTION_TARGET} au-dessus` :
                           `${PRODUCTION_TARGET - auditor.averageTasksPerDay} en dessous`
                         }
                       </span>
                     </div>
                   </div>
                 );
               })}
           </div>
         </div>

         {/* Analyse comparative */}
         <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
           <div className="bg-white rounded-xl border border-gray-200 p-6">
             <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-bold text-gray-900">🎯 Atteinte Objectifs</h3>
               <Target className="w-5 h-5 text-blue-500" />
             </div>
             <div className="space-y-4">
               <div className="text-center p-4 bg-green-50 rounded-lg">
                 <p className="text-3xl font-bold text-green-600">
                   {analytics.excellentPerformers.length + analytics.goodPerformers.length}
                 </p>
                 <p className="text-sm text-green-700">Objectif atteint</p>
                 <p className="text-xs text-gray-500">
                   {((analytics.excellentPerformers.length + analytics.goodPerformers.length) / Math.max(globalStats.activeAuditors, 1) * 100).toFixed(0)}% de l'équipe
                 </p>
               </div>
               <div className="text-center p-4 bg-red-50 rounded-lg">
                 <p className="text-3xl font-bold text-red-600">
                   {analytics.belowTarget.length + analytics.inactive.length}
                 </p>
                 <p className="text-sm text-red-700">En dessous</p>
                 <p className="text-xs text-gray-500">
                   Déficit: {(analytics.belowTarget.reduce((sum, a) => sum + (PRODUCTION_TARGET - a.averageTasksPerDay), 0) + (analytics.inactive.length * PRODUCTION_TARGET)).toFixed(0)} tâches/jour
                 </p>
               </div>
             </div>
           </div>

           <div className="bg-white rounded-xl border border-gray-200 p-6">
             <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-bold text-gray-900">⚡ Capacité Théorique</h3>
               <Zap className="w-5 h-5 text-purple-500" />
             </div>
             <div className="space-y-4">
               <div className="flex justify-between items-center">
                 <span className="text-gray-600">Capacité Max</span>
                 <span className="font-bold">{analytics.maxCapacity}/jour</span>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-gray-600">Production Actuelle</span>
                 <span className="font-bold">{analytics.currentUsage.toFixed(0)}/jour</span>
               </div>
               <div className="flex justify-between items-center">
                 <span className="text-gray-600">Potentiel Inexploité</span>
                 <span className={`font-bold ${
                   analytics.maxCapacity - analytics.currentUsage > 0 ? 'text-red-600' : 'text-green-600'
                 }`}>
                   {(analytics.maxCapacity - analytics.currentUsage).toFixed(0)}/jour
                 </span>
               </div>
               <div className="w-full bg-gray-200 rounded-full h-3 mt-4">
                 <div 
                   className="bg-gradient-to-r from-purple-500 to-purple-600 h-3 rounded-full"
                   style={{ width: `${analytics.capacityUtilization}%` }}
                 />
               </div>
               <p className="text-center text-sm text-gray-500">
                 Utilisation: {analytics.capacityUtilization.toFixed(1)}%
               </p>
             </div>
           </div>

           <div className="bg-white rounded-xl border border-gray-200 p-6">
             <div className="flex items-center justify-between mb-4">
               <h3 className="text-lg font-bold text-gray-900">🏆 Performance Moyenne</h3>
               <Award className="w-5 h-5 text-yellow-500" />
             </div>
             <div className="space-y-4">
               <div className="text-center p-4 bg-blue-50 rounded-lg">
                 <p className="text-3xl font-bold text-blue-600">
                   {analytics.avgProcessingProductivity.toFixed(0)}
                 </p>
                 <p className="text-sm text-blue-700">Tâches/jour moyenne</p>
                 <p className={`text-xs font-bold ${
                   analytics.avgProcessingProductivity >= PRODUCTION_TARGET ? 'text-green-600' : 'text-red-600'
                 }`}>
                   {analytics.avgProcessingProductivity >= PRODUCTION_TARGET ? 
                     `+${(analytics.avgProcessingProductivity - PRODUCTION_TARGET).toFixed(0)} au-dessus` :
                     `${(PRODUCTION_TARGET - analytics.avgProcessingProductivity).toFixed(0)} en dessous`
                   }
                 </p>
               </div>
               <div className="space-y-2">
                 <div className="flex justify-between text-sm">
                   <span>vs Objectif ({PRODUCTION_TARGET})</span>
                   <span className={`font-bold ${
                     analytics.avgProcessingProductivity >= PRODUCTION_TARGET ? 'text-green-600' : 'text-red-600'
                   }`}>
                     {((analytics.avgProcessingProductivity / PRODUCTION_TARGET) * 100).toFixed(0)}%
                   </span>
                 </div>
                 <div className="w-full bg-gray-200 rounded-full h-2">
                   <div 
                     className={`h-2 rounded-full ${
                       analytics.avgProcessingProductivity >= PRODUCTION_TARGET ? 'bg-green-500' : 'bg-red-500'
                     }`}
                     style={{ width: `${Math.min((analytics.avgProcessingProductivity / PRODUCTION_TARGET) * 100, 100)}%` }}
                   />
                 </div>
               </div>
             </div>
           </div>
         </div>
       </div>
     )}

     {/* ⚡ ANALYSE DE CAPACITÉ */}
     {selectedReport === 'capacity' && (
       <div className="space-y-6">
         {/* Vue d'ensemble de la capacité */}
         <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
           <div className="bg-white rounded-xl border border-gray-200 p-6">
             <h3 className="text-lg font-bold text-gray-900 mb-6">⚡ Analyse de Capacité Globale</h3>
             
             <div className="space-y-6">
               <div className="text-center p-6 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl text-white">
                 <h4 className="text-lg font-bold mb-2">Capacité Théorique Maximale</h4>
                 <p className="text-4xl font-bold">{analytics.maxCapacity}</p>
                 <p className="text-blue-100">tâches/jour</p>
                 <p className="text-xs text-blue-200 mt-2">
                   {globalStats.activeAuditors} auditeurs × {PRODUCTION_TARGET} tâches
                 </p>
               </div>

               <div className="grid grid-cols-2 gap-4">
                 <div className="text-center p-4 bg-green-50 rounded-lg">
                   <p className="text-2xl font-bold text-green-600">{analytics.currentUsage.toFixed(0)}</p>
                   <p className="text-sm text-green-700">Production Actuelle</p>
                 </div>
                 <div className="text-center p-4 bg-orange-50 rounded-lg">
                   <p className="text-2xl font-bold text-orange-600">{(analytics.maxCapacity - analytics.currentUsage).toFixed(0)}</p>
                   <p className="text-sm text-orange-700">Capacité Inutilisée</p>
                 </div>
               </div>

               <div className="space-y-2">
                 <div className="flex justify-between text-sm">
                   <span>Utilisation de la capacité</span>
                   <span className="font-bold">{analytics.capacityUtilization.toFixed(1)}%</span>
                 </div>
                 <div className="w-full bg-gray-200 rounded-full h-4">
                   <div 
                     className={`h-4 rounded-full transition-all duration-1000 ${
                       analytics.capacityUtilization >= 90 ? 'bg-gradient-to-r from-green-500 to-green-600' :
                       analytics.capacityUtilization >= 70 ? 'bg-gradient-to-r from-yellow-500 to-orange-500' :
                       'bg-gradient-to-r from-red-500 to-red-600'
                     }`}
                     style={{ width: `${Math.min(analytics.capacityUtilization, 100)}%` }}
                   />
                 </div>
               </div>
             </div>
           </div>

           <div className="bg-white rounded-xl border border-gray-200 p-6">
             <h3 className="text-lg font-bold text-gray-900 mb-6">📊 Répartition de la Charge</h3>
             
             <div className="space-y-4">
               {analytics.excellentPerformers.length > 0 && (
                 <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                   <div className="flex items-center justify-between mb-2">
                     <span className="font-medium text-yellow-900">🔥 Surperformants</span>
                     <span className="font-bold text-yellow-800">{analytics.excellentPerformers.length}</span>
                   </div>
                   <p className="text-sm text-yellow-700">
                     Production: {analytics.excellentPerformers.reduce((sum, a) => sum + a.averageTasksPerDay, 0).toFixed(0)}/jour
                   </p>
                   <div className="w-full bg-yellow-200 rounded-full h-2 mt-2">
                     <div 
                       className="bg-yellow-600 h-2 rounded-full"
                       style={{ 
                         width: `${(analytics.excellentPerformers.reduce((sum, a) => sum + a.averageTasksPerDay, 0) / analytics.maxCapacity) * 100}%` 
                       }}
                     />
                   </div>
                 </div>
               )}

               <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                 <div className="flex items-center justify-between mb-2">
                   <span className="font-medium text-green-900">✅ Dans la Norme</span>
                   <span className="font-bold text-green-800">{analytics.goodPerformers.length}</span>
                 </div>
                 <p className="text-sm text-green-700">
                   Production: {analytics.goodPerformers.reduce((sum, a) => sum + a.averageTasksPerDay, 0).toFixed(0)}/jour
                 </p>
                 <div className="w-full bg-green-200 rounded-full h-2 mt-2">
                   <div 
                     className="bg-green-600 h-2 rounded-full"
                     style={{ 
                       width: `${(analytics.goodPerformers.reduce((sum, a) => sum + a.averageTasksPerDay, 0) / analytics.maxCapacity) * 100}%` 
                     }}
                   />
                 </div>
               </div>

               <div className="p-4 bg-orange-50 rounded-lg border border-orange-200">
                 <div className="flex items-center justify-between mb-2">
                   <span className="font-medium text-orange-900">⚠️ Sous-Performance</span>
                   <span className="font-bold text-orange-800">{analytics.belowTarget.length}</span>
                 </div>
                 <p className="text-sm text-orange-700">
                   Production: {analytics.belowTarget.reduce((sum, a) => sum + a.averageTasksPerDay, 0).toFixed(0)}/jour
                 </p>
                 <div className="w-full bg-orange-200 rounded-full h-2 mt-2">
                   <div 
                     className="bg-orange-600 h-2 rounded-full"
                     style={{ 
                       width: `${(analytics.belowTarget.reduce((sum, a) => sum + a.averageTasksPerDay, 0) / analytics.maxCapacity) * 100}%` 
                     }}
                   />
                 </div>
               </div>

               {analytics.inactive.length > 0 && (
                 <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                   <div className="flex items-center justify-between mb-2">
                     <span className="font-medium text-red-900">❌ Inactifs</span>
                     <span className="font-bold text-red-800">{analytics.inactive.length}</span>
                   </div>
                   <p className="text-sm text-red-700">
                     Capacité perdue: {analytics.inactive.length * PRODUCTION_TARGET}/jour
                   </p>
                 </div>
               )}
             </div>
           </div>
         </div>
       </div>
     )}

     {/* 🎓 RECOMMANDATIONS DE COACHING */}
     {selectedReport === 'coaching' && (
       <div className="space-y-6">
         {/* Plan d'amélioration global */}
         <div className="bg-white rounded-xl border border-gray-200 p-6">
           <h3 className="text-lg font-bold text-gray-900 mb-6">🎓 Plan d'Amélioration Personnalisé</h3>
           
           <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
             <div>
               <h4 className="font-bold text-red-900 mb-4">🚨 Intervention Urgente Requise</h4>
               <div className="space-y-3">
                 {analytics.needsAttention.slice(0, 5).map((auditor) => (
                   <div key={auditor.auditor_id} className="p-4 bg-red-50 rounded-lg border-l-4 border-red-500">
                     <div className="flex items-center justify-between mb-2">
                       <span className="font-medium text-red-900">{auditor.auditor.full_name}</span>
                       <span className="text-sm font-bold text-red-700">
                         {auditor.averageTasksPerDay}/jour
                       </span>
                     </div>
                     <div className="text-sm text-red-700 space-y-1">
                       {auditor.averageTasksPerDay === 0 && <p>• ❌ Inactif - Aucune tâche traitée</p>}
                       {auditor.averageTasksPerDay < PRODUCTION_TARGET * 0.5 && auditor.averageTasksPerDay > 0 && <p>• 📉 Productivité très faible (&lt;50% norme)</p>}
                       {auditor.averageTasksPerDay < PRODUCTION_TARGET && auditor.averageTasksPerDay >= PRODUCTION_TARGET * 0.5 && <p>• ⚠️ Sous la norme (-{(PRODUCTION_TARGET - auditor.averageTasksPerDay).toFixed(0)} tâches/jour)</p>}
                       {auditor.validationRate < 70 && <p>• 📊 Taux validation faible ({auditor.validationRate}%)</p>}
                       {auditor.avgProcessingTime > globalStats.averageProcessingTime * 1.5 && <p>• ⏱️ Temps traitement lent ({auditor.avgProcessingTime}min vs {globalStats.averageProcessingTime}min)</p>}
                       <p className="font-medium text-red-800">
                         → Formation intensive + suivi quotidien
                       </p>
                     </div>
                   </div>
                 ))}
               </div>
             </div>

             <div>
               <h4 className="font-bold text-yellow-900 mb-4">⚡ Optimisation Performance</h4>
               <div className="space-y-3">
                 {auditorStats
                   .filter(a => a.averageTasksPerDay >= PRODUCTION_TARGET * 0.8 && a.averageTasksPerDay < PRODUCTION_TARGET)
                   .slice(0, 5)
                   .map((auditor) => (
                     <div key={auditor.auditor_id} className="p-4 bg-yellow-50 rounded-lg border-l-4 border-yellow-500">
                       <div className="flex items-center justify-between mb-2">
                         <span className="font-medium text-yellow-900">{auditor.auditor.full_name}</span>
                         <span className="text-sm font-bold text-yellow-700">
                           {auditor.averageTasksPerDay}/jour
                         </span>
                       </div>
                       <div className="text-sm text-yellow-700 space-y-1">
                         <p>• 🎯 Proche de l'objectif (+{(PRODUCTION_TARGET - auditor.averageTasksPerDay).toFixed(0)} pour atteindre {PRODUCTION_TARGET})</p>
                         {auditor.validationRate >= 80 && <p>• ✅ Bonne qualité ({auditor.validationRate}%)</p>}
                         {auditor.avgProcessingTime <= globalStats.averageProcessingTime && <p>• ⚡ Vitesse correcte ({auditor.avgProcessingTime}min)</p>}
                         <p className="font-medium text-yellow-800">
                           → Coaching léger + motivation
                         </p>
                       </div>
                     </div>
                   ))}
               </div>
             </div>
           </div>
         </div>

         {/* Tableau de bord de suivi */}
         <div className="bg-white rounded-xl border border-gray-200 p-6">
           <h3 className="text-lg font-bold text-gray-900 mb-6">📊 Tableau de Bord de Suivi</h3>
           
           <div className="overflow-x-auto">
             <table className="w-full text-sm">
               <thead>
                 <tr className="border-b border-gray-200">
                   <th className="text-left py-3 px-4 font-medium text-gray-700">Auditeur</th>
                   <th className="text-center py-3 px-4 font-medium text-gray-700">Actuel</th>
                   <th className="text-center py-3 px-4 font-medium text-gray-700">Objectif 30j</th>
                   <th className="text-center py-3 px-4 font-medium text-gray-700">Écart</th>
                   <th className="text-center py-3 px-4 font-medium text-gray-700">Priorité</th>
                   <th className="text-center py-3 px-4 font-medium text-gray-700">Actions</th>
                 </tr>
               </thead>
               <tbody>
                 {analytics.needsAttention.slice(0, 10).map((auditor) => {
                   const target30Days = Math.min(PRODUCTION_TARGET, auditor.averageTasksPerDay + 50);
                   const gap = target30Days - auditor.averageTasksPerDay;
                   const priority = auditor.averageTasksPerDay === 0 ? 'Critique' :
                                  auditor.averageTasksPerDay < PRODUCTION_TARGET * 0.5 ? 'Haute' :
                                  auditor.averageTasksPerDay < PRODUCTION_TARGET * 0.8 ? 'Moyenne' : 'Faible';
                   
                   return (
                     <tr key={auditor.auditor_id} className="border-b border-gray-100 hover:bg-gray-50">
                       <td className="py-3 px-4">
                         <div className="flex items-center space-x-3">
                           <div className={`w-8 h-8 bg-gradient-to-r ${getPerformanceColor(auditor.averageTasksPerDay, auditor.validationRate)} rounded-full flex items-center justify-center`}>
                             <span className="text-white text-xs font-bold">
                               {auditor.auditor.full_name.split(' ').map(n => n[0]).join('').toUpperCase()}
                             </span>
                           </div>
                           <span className="font-medium">{auditor.auditor.full_name}</span>
                         </div>
                       </td>
                       <td className="py-3 px-4 text-center">
                         <span className={`font-bold px-2 py-1 rounded ${
                           auditor.averageTasksPerDay === 0 ? 'bg-red-100 text-red-800' :
                           auditor.averageTasksPerDay < PRODUCTION_TARGET * 0.5 ? 'bg-orange-100 text-orange-800' :
                           'bg-yellow-100 text-yellow-800'
                         }`}>
                           {auditor.averageTasksPerDay}/jour
                         </span>
                       </td>
                       <td className="py-3 px-4 text-center font-bold">{target30Days}/jour</td>
                       <td className="py-3 px-4 text-center">
                         <span className="text-red-600 font-bold">+{gap.toFixed(0)}</span>
                       </td>
                       <td className="py-3 px-4 text-center">
                         <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                           priority === 'Critique' ? 'bg-red-500 text-white' :
                           priority === 'Haute' ? 'bg-orange-500 text-white' :
                           priority === 'Moyenne' ? 'bg-yellow-500 text-white' :
                           'bg-blue-500 text-white'
                         }`}>
                           {priority}
                         </span>
                       </td>
                       <td className="py-3 px-4 text-center">
                         <div className="flex flex-wrap gap-1 justify-center">
                           {auditor.averageTasksPerDay === 0 && (
                             <span className="px-2 py-1 bg-red-100 text-red-700 rounded text-xs">Réactivation</span>
                           )}
                           {auditor.validationRate < 70 && (
                             <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs">Formation Qualité</span>
                           )}
                           {auditor.avgProcessingTime > globalStats.averageProcessingTime * 1.5 && (
                             <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded text-xs">Formation Vitesse</span>
                           )}
                           <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs">Mentoring</span>
                         </div>
                       </td>
                     </tr>
                   );
                 })}
               </tbody>
             </table>
           </div>
         </div>
       </div>
     )}
   </div>
 );
};

export default ReportsView;